import React, { useLayoutEffect, useState } from 'react';

import { TextColors } from '../../components/ui/color-picker/color-picker';
import { getTextFormat } from '../../utils/text-formatting/get-text-format';
import { toUrl } from './../../utils/to-url';
import { ContextMenu } from './use-mindmap-context-menu';

export interface TextFormatting {
  toggleSelectedTextFormat: (formatInput: FormatChangeInput) => void;
  selectedTextFormats: Format | undefined;
}

type UseTextFormatting = {
  contextMenu: ContextMenu;
};

type BoldFormat = {
  type: 'bold';
  key: 'font-weight';
  set: '700';
  unset: '400';
  value: boolean;
};

type ItalicFormat = {
  type: 'italic';
  key: 'font-style';
  set: 'italic';
  unset: 'normal';
  value: boolean;
};

type SizeFormat = {
  type: 'size';
  activeFontSize: number;
  key: 'font-size';
};

type LightFormat = {
  type: 'light';
  key: 'font-weight';
  set: '100';
  unset: '400';
  value: boolean;
};

type MonospaceFormat = {
  type: 'monospace';
  key: 'font-family';
  set: 'monospace';
  unset: 'var(--font-family)';
  value: boolean;
};

type ColorFormat = {
  type: 'color';
  key: 'color';
  unset: 'var(--text-color)';
  activeColor: TextColors;
};

type HighlightFormat = {
  type: 'highlight';
  key: 'background-color';
  unset: 'var(--surface-0)';
  activeHighlight?: TextColors;
};

type LinkFormat = {
  type: 'link';
  link: boolean;
};

export type Format = {
  bold: BoldFormat;
  italic: ItalicFormat;
  size: SizeFormat;
  light: LightFormat;
  monospace: MonospaceFormat;
  color: ColorFormat;
  highlight: HighlightFormat;
  link: LinkFormat;
};

const FONT_SIZES = [4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48];

type FormatChangeInput =
  | { type: 'bold' | 'italic' | 'light' | 'monospace' | 'link' }
  | { type: 'size'; enlarge: boolean }
  | { type: 'color' | 'highlight'; color: TextColors };

export const useMindmapTextFormatting = ({ contextMenu: { activeMenu } }: UseTextFormatting): TextFormatting => {
  const [selectedTextFormats, setSelectedTextFormats] = useState<Format>();

  // update the selectedTextFormats state when the format-text menu is shown
  useLayoutEffect(() => {
    if (activeMenu?.type == 'format-text') {
      // update the formatting info about the currently selected text
      const startElement = window.getSelection()?.anchorNode?.parentElement;
      if (startElement) setSelectedTextFormats(getTextFormat(startElement));
    }
  }, [activeMenu?.type]);

  const toggleSelectedTextFormat = (request: FormatChangeInput) => {
    // check if the text format of the currently selected text was saved into selectedTextFormats
    if (!selectedTextFormats) {
      console.error('Called toggleSelectedTextFormat without setting selectedTextFormats first.', {
        request,
        selectedTextFormats,
      });
      return;
    }

    // check if the requested format type exists in the text format of the currently selected text
    const format = selectedTextFormats[request.type];
    if (!format) {
      console.error('Called toggleSelectedTextFormat with an invalid format request type.', { request });
      return;
    }

    // check if window.getSelection is populated
    let selection = window.getSelection();
    if (!selection) {
      console.error('Called toggleSelectedTextFormat without window.getSelection populated.', { selection });
      return;
    }

    // either toggle a link or toggle a style
    if (format?.type === 'link') toggleLink(selection, format, selectedTextFormats);
    else toggleStyle(selection, format, selectedTextFormats, request);

    // update edited html in temporary node-text renderer via activeMenu callback
    if (activeMenu?.type == 'format-text') activeMenu.updateHtml();
  };

  // removes a link if the selection selects a link
  // creates a new link out of the selection if the selection doesn't select a link
  const toggleLink = (selection: Selection, format: LinkFormat, selectedTextFormats: Format) => {
    const startElement = selection.anchorNode?.parentElement;
    if (startElement) {
      // remove potential link(s) from parent(s) by replacing <a> with <span>
      let createdElement: HTMLElement | undefined;
      let currentElement: HTMLElement | undefined = startElement;
      do {
        const isLink = currentElement.nodeName == 'A';
        if (isLink) {
          createdElement = document.createElement('span');
          createdElement.innerHTML = currentElement.innerHTML;
          currentElement.replaceWith(createdElement);
        }

        if (currentElement.parentElement) currentElement = currentElement.parentElement;
        else break;
      } while (currentElement && !currentElement.classList.contains('node-text-root'));

      // extract content from selection range
      const selectedRange = selection.getRangeAt(0);
      const selectedText = selectedRange.toString();
      let selectedContent = selectedRange.extractContents();

      // remove links inside of selection range
      const allLinkChildren = selectedContent.querySelectorAll('a');
      Array.from(allLinkChildren).forEach((child) => {
        createdElement = document.createElement('span');
        createdElement.innerHTML = child.innerHTML;
        child.replaceWith(createdElement);
      });

      if (format.link) {
        // an existing link was deleted
        setSelectedTextFormats({ ...selectedTextFormats, link: { ...format, link: false } });

        // add link with content back to range
        selectedRange.insertNode(selectedContent);

        // reselect content
        if (createdElement) {
          const range = document.createRange();
          range.selectNodeContents(createdElement);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else {
        // create a new link
        const link = document.createElement('a');
        link.href = toUrl(selectedText);

        // add link with content back to range
        link.appendChild(selectedContent);
        selectedRange.insertNode(link);
        setSelectedTextFormats({ ...selectedTextFormats, link: { ...format, link: true } });
      }
    }
  };

  const toggleStyle = (
    selection: Selection,
    format: BoldFormat | ItalicFormat | SizeFormat | LightFormat | MonospaceFormat | ColorFormat | HighlightFormat,
    selectedTextFormats: Format,
    request: FormatChangeInput,
  ) => {
    // check what type of format should be toggled or changed
    const isReqSizeChange = request.type == 'size' && format.type == 'size';
    const isReqColorChange = request.type == 'color' && format.type == 'color';
    const isReqHighlightChange = request.type == 'highlight' && format.type == 'highlight';
    const isReqBoldChange = request.type === 'bold' && format.type == 'bold';
    const isReqItalicChange = request.type === 'italic' && format.type == 'italic';
    const isReqLightChange = request.type === 'light' && format.type == 'light';
    const isReqMonospaceChange = request.type === 'monospace' && format.type == 'monospace';

    // create a new span where the new styles get applied.
    // TODO: you could optimize this by using the existing hierarchy and
    // just creating new spans or deleting existing spans when needed
    const span = document.createElement('span');

    if (isReqSizeChange) {
      // get current font-size and size up or down according to the request
      // by selecting the next larger or smaller value from FONT_SIZES
      const currentSize = format.activeFontSize;
      const indexOfCurrentSize = FONT_SIZES.indexOf(currentSize);
      let newFontSize = request.enlarge
        ? indexOfCurrentSize == FONT_SIZES.length - 1
          ? currentSize
          : FONT_SIZES[indexOfCurrentSize + 1]
        : indexOfCurrentSize == 0
        ? currentSize
        : FONT_SIZES[indexOfCurrentSize - 1];

      // @ts-ignore
      span.style[format.key] = `${newFontSize}px`;
      setSelectedTextFormats({ ...selectedTextFormats, size: { ...format, activeFontSize: newFontSize } });
    } else if (isReqColorChange) {
      let color = request.color;
      const currentColor = format.activeColor;
      // if the current color was selected unset it, otherwise use the new color directly
      let newColor = (color == currentColor ? format.unset : color) as TextColors;
      // @ts-ignore
      span.style[format.key] = newColor;
      setSelectedTextFormats({ ...selectedTextFormats, color: { ...format, activeColor: newColor } });
    } else if (isReqHighlightChange) {
      let color = request.color;
      const currentHighlight = format.activeHighlight;
      // if the current color was selected unset it, otherwise use the new color directly
      let newHighlight = (color == currentHighlight ? format.unset : color) as TextColors;
      // @ts-ignore
      span.style[format.key] = newHighlight;
      setSelectedTextFormats({ ...selectedTextFormats, highlight: { ...format, activeHighlight: newHighlight } });
    } else if (isReqBoldChange) {
      const newBold = format.value ? format.unset : format.set;
      // @ts-ignore
      span.style[format.key] = newBold;
      setSelectedTextFormats({
        ...selectedTextFormats,
        bold: { ...format, value: newBold === format.set },
        // unset light because text can be either bold or light
        light: { ...selectedTextFormats.light, type: 'light', value: false },
      });
    } else if (isReqLightChange) {
      const newLight = format.value ? format.unset : format.set;
      // @ts-ignore
      span.style[format.key] = newLight;
      setSelectedTextFormats({
        ...selectedTextFormats,
        light: { ...format, value: newLight === format.set },
        // unset bold because text can be either bold or light
        bold: { ...selectedTextFormats.bold, type: 'bold', value: false },
      });
    } else if (isReqItalicChange) {
      const newItalic = format.value ? format.unset : format.set;
      // @ts-ignore
      span.style[format.key] = newItalic;
      setSelectedTextFormats({ ...selectedTextFormats, italic: { ...format, value: newItalic === format.set } });
    } else if (isReqMonospaceChange) {
      const newMonospace = format.value ? format.unset : format.set;
      // @ts-ignore
      span.style[format.key] = newMonospace;
      setSelectedTextFormats({
        ...selectedTextFormats,
        monospace: { ...format, value: newMonospace === format.set },
      });
    }

    // extract content from selection range
    const selectionRange = selection?.getRangeAt(0);
    const selectedContent = selectionRange.extractContents();

    // remove all spans from children with the currently modified key
    const childElements = selectedContent.querySelectorAll(`span[style*="${format.key}"]`);
    // @ts-ignore
    childElements.forEach((element) => element.style.removeProperty(format.key));

    // add span with content back to range
    span.appendChild(selectedContent);
    selectionRange.insertNode(span);
  };

  return { toggleSelectedTextFormat, selectedTextFormats };
};
